export const SI_ROOT_ROUTE = "salesIntelligence";

const SI_AD_INTELLIGENCE_ROOT = `${SI_ROOT_ROUTE}-adintelligence`;

// Leaf route-name fragments are composed into the route objects below and are not part of the
// cross-solution public surface, so they stay module-private. The composed `SI_*_ROUTES` objects
// are the exported surface.
const SI_AD_INTELLIGENCE_AA_ROUTE = `${SI_ROOT_ROUTE}-advertiseractivity`;
const SI_AD_INTELLIGENCE_SOCIAL_ROUTE = `${SI_ROOT_ROUTE}-social-channels`;
const SI_AD_INTELLIGENCE_LLMS_ROUTE = `${SI_ROOT_ROUTE}-llms-channels`;
const SI_AD_INTELLIGENCE_SEARCH_ROUTE = `${SI_AD_INTELLIGENCE_ROOT}-searchadvertising`;
const SI_AD_INTELLIGENCE_DISPLAY_ROOT = `${SI_AD_INTELLIGENCE_ROOT}-displayadvertising`;
const SI_AD_INTELLIGENCE_MP_ROUTE = `${SI_ROOT_ROUTE}-monitorandprotect`;
const SI_AD_INTELLIGENCE_TRADEMARK_ABSTRACT_ROUTE = `${SI_AD_INTELLIGENCE_MP_ROUTE}-trademark-abstract`;
const SI_AD_INTELLIGENCE_TRADEMARK_ROOT_ROUTE = `${SI_AD_INTELLIGENCE_MP_ROUTE}-trademark-root`;
const SI_AD_INTELLIGENCE_PA_ROUTE = `${SI_ROOT_ROUTE}-publisheranalysis`;

const SI_MNP_TRADEMARK_URL_PREFIX = "/monitorandprotect/trademark";

// --- Advertiser Activity (SI copy) ---
export const SI_AA_ROUTES = {
    container: SI_AD_INTELLIGENCE_AA_ROUTE,
    root: `${SI_AD_INTELLIGENCE_AA_ROUTE}.root`,
    frame: `${SI_AD_INTELLIGENCE_AA_ROUTE}.root.frame`,
    home: `${SI_AD_INTELLIGENCE_AA_ROUTE}.homepage`,
    overview: `${SI_AD_INTELLIGENCE_AA_ROUTE}.root.frame.overview`,
} as const;

// --- Display Advertising (SI copy) ---
export const SI_DISPLAY_ROUTES = {
    container: SI_AD_INTELLIGENCE_DISPLAY_ROOT,
    root: `${SI_AD_INTELLIGENCE_DISPLAY_ROOT}.root`,
    frame: `${SI_AD_INTELLIGENCE_DISPLAY_ROOT}.root.frame`,
    home: `${SI_AD_INTELLIGENCE_DISPLAY_ROOT}.root.home`,
    overview: `${SI_AD_INTELLIGENCE_DISPLAY_ROOT}.root.frame.overview`,
    campaigns: `${SI_AD_INTELLIGENCE_DISPLAY_ROOT}.root.frame.campaigns`,
    ads: `${SI_AD_INTELLIGENCE_DISPLAY_ROOT}.root.frame.ads`,
    publishers: `${SI_AD_INTELLIGENCE_DISPLAY_ROOT}.root.frame.publishers`,
} as const;

// --- Search Advertising (SI copy) ---
export const SI_SEARCH_ROUTES = {
    container: SI_AD_INTELLIGENCE_SEARCH_ROUTE,
    root: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root`,
    frame: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.frame`,
    pageFrame: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.pageFrame`,
    keywordFrame: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.keywordFrame`,
    home: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.home`,
    monitorKeywordsHome: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.monitorKeywordsHome`,
    monitorKeywordsRoot: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.monitorKeywordsRoot`,
    monitorKeywords: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.monitorKeywordsRoot.monitorKeywords`,
    keywordLandingPages: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.keywordFrame.keywordLandingPages`,
    serpPlayers: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.keywordFrame.serpPlayers`,
    keywordAds: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.keywordFrame.ads`,
    topics: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.frame.topics`,
    keywords: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.pageFrame.keywords`,
    landingPages: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.pageFrame.landingPages`,
    textAndShoppingAds: `${SI_AD_INTELLIGENCE_SEARCH_ROUTE}.root.pageFrame.textAndShoppingAds`,
} as const;

// --- Social Channels (SI copy) ---
export const SI_SOCIAL_ROUTES = {
    abstract: SI_AD_INTELLIGENCE_SOCIAL_ROUTE,
    ads: `${SI_AD_INTELLIGENCE_SOCIAL_ROUTE}.ads`,
} as const;

// --- LLMs Channels (SI copy) ---
export const SI_LLMS_ROUTES = {
    abstract: SI_AD_INTELLIGENCE_LLMS_ROUTE,
    promo: `${SI_AD_INTELLIGENCE_LLMS_ROUTE}.promo`,
} as const;

// --- Publisher Analysis (SI copy) ---
export const SI_PA_ROUTES = {
    container: SI_AD_INTELLIGENCE_PA_ROUTE,
    root: `${SI_AD_INTELLIGENCE_PA_ROUTE}.root`,
    frame: `${SI_AD_INTELLIGENCE_PA_ROUTE}.root.frame`,
    home: `${SI_AD_INTELLIGENCE_PA_ROUTE}.homepage`,
    overview: `${SI_AD_INTELLIGENCE_PA_ROUTE}.root.frame.overview`,
    advertisers: `${SI_AD_INTELLIGENCE_PA_ROUTE}.root.frame.advertisers`,
    ads: `${SI_AD_INTELLIGENCE_PA_ROUTE}.root.frame.ads`,
} as const;

// --- Monitor & Protect — Keywords (SI copy) ---
/* eslint-disable @typescript-eslint/naming-convention */
export const SI_MNP_ROUTES: Record<
    "COMPETITORS" | "ABSTRACT" | "HOME" | "OVERVIEW" | "KEYWORDS" | "ADS" | "SETTINGS",
    { camelCase: string; kebabCase: string; stateName: string; url: string }
> = {
    COMPETITORS: {
        camelCase: "competitorsReport",
        kebabCase: "competitors-report",
        stateName: `${SI_AD_INTELLIGENCE_MP_ROUTE}.competitors-report`,
        url: "/competitors",
    },
    ABSTRACT: {
        camelCase: "monitorAndProtectAbstract",
        kebabCase: "monitor-and-protect-abstract",
        stateName: SI_AD_INTELLIGENCE_MP_ROUTE,
        url: "/monitorandprotect",
    },
    HOME: {
        camelCase: "monitorAndProtectHome",
        kebabCase: "monitor-and-protect-home",
        stateName: `${SI_AD_INTELLIGENCE_MP_ROUTE}.home`,
        url: "/home",
    },
    OVERVIEW: {
        camelCase: "monitorAndProtectOverview",
        kebabCase: "monitor-and-protect-overview",
        stateName: `${SI_AD_INTELLIGENCE_MP_ROUTE}.overview`,
        url: "/overview",
    },
    KEYWORDS: {
        camelCase: "monitorAndProtectKeywords",
        kebabCase: "monitor-and-protect-keywords",
        stateName: `${SI_AD_INTELLIGENCE_MP_ROUTE}.keywords`,
        url: "/keywords",
    },
    ADS: {
        camelCase: "monitorAndProtectAds",
        kebabCase: "monitor-and-protect-ads",
        stateName: `${SI_AD_INTELLIGENCE_MP_ROUTE}.ads-tracker`,
        url: "/ads-tracker",
    },
    SETTINGS: {
        camelCase: "settings",
        kebabCase: "settings",
        stateName: `${SI_AD_INTELLIGENCE_MP_ROUTE}.settings`,
        url: "/settings",
    },
} as const;

// --- Monitor & Protect — Trademark (SI copy) ---
export const SI_MNP_TRADEMARK_ROUTES = {
    ABSTRACT: {
        camelCase: "monitorAndProtectAbstract",
        kebabCase: "monitor-and-protect-abstract",
        stateName: SI_AD_INTELLIGENCE_TRADEMARK_ABSTRACT_ROUTE,
        url: SI_MNP_TRADEMARK_URL_PREFIX,
    },
    VIOLATIONS: {
        camelCase: "violations",
        kebabCase: "violations",
        stateName: `${SI_AD_INTELLIGENCE_TRADEMARK_ROOT_ROUTE}-violations`,
        url: `${SI_MNP_TRADEMARK_URL_PREFIX}/violations`,
    },
    TRADEMARKS: {
        camelCase: "trademarks",
        kebabCase: "trademarks",
        stateName: `${SI_AD_INTELLIGENCE_TRADEMARK_ABSTRACT_ROUTE}.trademarks`,
        url: "/trademarks",
    },
    TRADEMARKS_CREATE: {
        camelCase: "create",
        kebabCase: "create",
        stateName: `${SI_AD_INTELLIGENCE_TRADEMARK_ABSTRACT_ROUTE}.trademarksCreate`,
        url: "/trademarks/create",
    },
    TRADEMARKS_EDIT: {
        camelCase: "edit",
        kebabCase: "edit",
        stateName: `${SI_AD_INTELLIGENCE_TRADEMARK_ABSTRACT_ROUTE}.trademarksEdit`,
        url: "/trademarks/edit/:trademarkId",
    },
} as const;
/* eslint-enable @typescript-eslint/naming-convention */
